import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './devolucion/components/Header';
import SearchForm from './devolucion/components/SearchForm';
import ConfirmModal from './devolucion/components/ConfirmModal';

/**
 * Componente de Gestión de Devoluciones
 * 
 * Cambios principales para integración con backend actualizado:
 * - Usa endpoints separados: /ventas/admin/:id y /devoluciones/venta/:id
 * - Maneja correctamente `createdAt` en lugar de `fecha_venta`
 * - Soporta devoluciones múltiples y parciales
 * - Procesa estructura de respuesta: { devoluciones: [], total_devuelto: 0 }
 * - Previene sobre-devoluciones con validaciones del backend
 * - Auto-refresca la información después de procesar devoluciones
 */

const DevolucionVenta = () => {
    const { id_venta } = useParams(); // Si viene desde ruta con parámetro
    const navigate = useNavigate();
    const { token, user } = useAuth(); // Obtener también el usuario del contexto

    // Estados del componente según las especificaciones actualizadas
    const [ventaBuscada, setVentaBuscada] = useState(null);
    const [productosDevolucion, setProductosDevolucion] = useState([]);
    const [formaDevolucion, setFormaDevolucion] = useState('efectivo');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
    const [idVentaBuscar, setIdVentaBuscar] = useState(id_venta || '');
    const [procesando, setProcesando] = useState(false);
    
    // Nuevos estados para funcionalidades avanzadas
    const [historialDevoluciones, setHistorialDevoluciones] = useState([]);
    const [mostrarHistorial, setMostrarHistorial] = useState(false);
    const [devolucionesPrevias, setDevolucionesPrevias] = useState({});
    const [estadisticasVenta, setEstadisticasVenta] = useState({
        total_final: 0,
        total_antes: 0,
        total_ya_devuelto: 0,
        total_disponible_devolucion: 0
    });

    // Validación de caja abierta
    const [cajaAbierta, setCajaAbierta] = useState(null);
    const [verificandoCaja, setVerificandoCaja] = useState(true);

    // Verificar si hay una caja abierta al cargar el componente
    useEffect(() => {
        const verificarCajaAbierta = async () => {
            try {
                const cajaId = localStorage.getItem('cajaAbiertaId');
                
                if (!cajaId) {
                    setCajaAbierta(null);
                    setVerificandoCaja(false);
                    return;
                }

                // Verificar que la caja almacenada sigue siendo válida
                const response = await axios.get(`${import.meta.env.VITE_API_URL}/caja/${cajaId}`, {
                    headers: { 'x-auth-token': token }
                });

                if (response.data && response.data.estado === 'abierta') {
                    setCajaAbierta(response.data);
                } else {
                    // Si la caja no está abierta, buscar otra abierta
                    localStorage.removeItem('cajaAbiertaId');
                    const buscarResponse = await axios.get(`${import.meta.env.VITE_API_URL}/caja/abierta`, {
                        headers: { 'x-auth-token': token }
                    });
                    
                    if (buscarResponse.data) {
                        setCajaAbierta(buscarResponse.data);
                        localStorage.setItem('cajaAbiertaId', buscarResponse.data.id);
                    } else {
                        setCajaAbierta(null);
                    }
                }
            } catch (error) {
                console.error('Error al verificar caja abierta:', error);
                setCajaAbierta(null);
                localStorage.removeItem('cajaAbiertaId');
            } finally {
                setVerificandoCaja(false);
            }
        };

        if (token) {
            verificarCajaAbierta();
        }
    }, [token]);

    // Obtener historial de devoluciones de una venta (ya no se usa directamente pero se mantiene para compatibilidad)
    const obtenerHistorialDevoluciones = async (idVenta) => {
        try {
            const response = await axios.get(`${import.meta.env.VITE_API_URL}/devoluciones/venta/${idVenta}`, {
                headers: { 'x-auth-token': token }
            });
            return response.data;
        } catch (err) {
            console.error('Error al obtener historial de devoluciones:', err);
            return [];
        }
    };

    // Buscar venta por ID con información completa de devoluciones
    const buscarVenta = async (idVenta) => {
        if (!idVenta) {
            setError('Por favor ingresa un ID de venta válido');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Temporalmente usar el endpoint básico de venta y obtener devoluciones por separado
            const [ventaResponse, devolucionesResponse] = await Promise.all([
                axios.get(`${import.meta.env.VITE_API_URL}/ventas/admin/${idVenta}`, {
                    headers: { 'x-auth-token': token }
                }),
                axios.get(`${import.meta.env.VITE_API_URL}/devoluciones/venta/${idVenta}`, {
                    headers: { 'x-auth-token': token }
                })
            ]);

            console.log('Respuesta venta:', ventaResponse.data); // Debug log
            console.log('Respuesta devoluciones:', devolucionesResponse.data); // Debug log
            console.log('Tipo de devoluciones:', typeof devolucionesResponse.data, Array.isArray(devolucionesResponse.data)); // Debug type
            
            const venta = ventaResponse.data.venta;
            
            // El backend devuelve { devoluciones: [], total_devuelto: 0 }
            // Extraer el array de devoluciones correctamente
            let historial_devoluciones = [];
            if (devolucionesResponse.data && devolucionesResponse.data.devoluciones) {
                // Si viene con la estructura { devoluciones: [], total_devuelto: 0 }
                historial_devoluciones = Array.isArray(devolucionesResponse.data.devoluciones) 
                    ? devolucionesResponse.data.devoluciones 
                    : [];
            } else if (Array.isArray(devolucionesResponse.data)) {
                // Si viene directamente como array
                historial_devoluciones = devolucionesResponse.data;
            } else {
                // Fallback a array vacío
                historial_devoluciones = [];
            }
            
            console.log('Historial devoluciones procesado:', historial_devoluciones); // Debug log
            
            // Validar que tenemos los datos necesarios
            if (!venta) {
                throw new Error('No se recibieron datos de la venta');
            }
            
            setVentaBuscada(venta);
            setHistorialDevoluciones(historial_devoluciones);

            // Calcular estadísticas - el backend guarda `venta.total` como total final (con descuento aplicado)
            const totalFinal = parseFloat(venta.total) || 0; // total después del descuento
            const descuentoVenta = parseFloat(venta.descuento) || 0;
            const totalAntes = totalFinal + descuentoVenta; // monto antes de aplicar el descuento
            const totalYaDevuelto = devolucionesResponse.data?.total_devuelto 
                ? parseFloat(devolucionesResponse.data.total_devuelto) 
                : (Array.isArray(historial_devoluciones) ? 
                    historial_devoluciones.reduce((total, dev) => total + parseFloat(dev.monto_devuelto || 0), 0) : 0);
            const totalDisponibleDevolucion = Math.max(0, totalFinal - totalYaDevuelto);

            setEstadisticasVenta({
                total_final: totalFinal,
                total_antes: totalAntes,
                total_ya_devuelto: totalYaDevuelto,
                total_disponible_devolucion: totalDisponibleDevolucion
            });

            // Procesar productos manualmente
            // Repartir el descuento total proporcionalmente según el monto bruto de cada línea
            const detalles = venta.detalles_venta || [];
            const totalBruto = detalles.reduce((s, d) => s + (parseFloat(d.cantidad || 0) * (parseFloat(d.precio || 0) || 0)), 0);

            const productosParaDevolucion = detalles.map(detalle => {
                const cantidadOriginal = parseFloat(detalle.cantidad || 0);
                
                // Calcular cantidad ya devuelta de este producto
                const devolucionesProducto = Array.isArray(historial_devoluciones) ? 
                    historial_devoluciones.filter(d => d.id_producto === detalle.producto.id) : [];
                const cantidadYaDevuelta = devolucionesProducto.reduce((total, d) => total + parseFloat(d.cantidad_devuelta || 0), 0);
                const cantidadDisponible = Math.max(0, cantidadOriginal - cantidadYaDevuelta);
                // Precio bruto de la línea (cantidad * precio unitario bruto)
                const precioBrutoUnit = parseFloat(detalle.precio || 0);
                const montoBrutoLinea = cantidadOriginal * precioBrutoUnit;

                // Proporción del descuento que corresponde a esta línea
                let descuentoLinea = 0;
                if (descuentoVenta > 0 && totalBruto > 0) {
                    descuentoLinea = (montoBrutoLinea / totalBruto) * descuentoVenta;
                    // No permitir descuento mayor al bruto de la línea
                    descuentoLinea = Math.max(0, Math.min(descuentoLinea, montoBrutoLinea));
                }

                // Precio unitario efectivo después de repartir el descuento de la línea
                const precioUnitarioEfectivo = cantidadOriginal > 0
                    ? (montoBrutoLinea - descuentoLinea) / cantidadOriginal
                    : precioBrutoUnit;

                return {
                    id_producto: detalle.producto.id,
                    nombre_producto: detalle.producto.nombre_producto,
                    cantidad_original: cantidadOriginal,
                    cantidad_ya_devuelta: cantidadYaDevuelta,
                    cantidad_disponible: cantidadDisponible,
                    // precio_unitario: ahora representa el precio efectivo por unidad (ya con su parte del descuento)
                    precio_unitario: precioUnitarioEfectivo,
                    precio_unitario_bruto: precioBrutoUnit,
                    cantidad_devuelta: 0,
                    motivo: '',
                    seleccionado: false,
                    monto_devolucion: 0,
                    estado_devolucion: cantidadDisponible <= 0 ? 'completamente_devuelto' : 
                                     cantidadYaDevuelta > 0 ? 'parcialmente_devuelto' : 'sin_devolver',
                    devoluciones_previas: devolucionesProducto.map(d => ({
                        fecha: d.createdAt,
                        cantidad: parseFloat(d.cantidad_devuelta),
                        motivo: d.motivo,
                        monto: parseFloat(d.monto_devuelto)
                    })),
                    deshabilitado: cantidadDisponible <= 0
                };
            });

            setProductosDevolucion(productosParaDevolucion);
        } catch (err) {
            console.error('Error al buscar venta:', err);
            if (err.response?.status === 404) {
                setError('Venta no encontrada');
            } else {
                setError('Error al buscar la venta. Intenta nuevamente.');
            }
            setVentaBuscada(null);
            setProductosDevolucion([]);
            setHistorialDevoluciones([]);
        } finally {
            setLoading(false);
        }
    };

    // Actualizar cantidad a devolver con validaciones mejoradas
    const actualizarCantidadDevolucion = (idProducto, cantidad) => {
        const cantidadNum = parseFloat(cantidad) || 0;

        setProductosDevolucion(prev => prev.map(producto => {
            if (producto.id_producto === idProducto) {
                // Validar que no exceda la cantidad disponible
                const nuevaCantidad = Math.max(0, Math.min(cantidadNum, producto.cantidad_disponible));
                const montoDevolucion = nuevaCantidad * producto.precio_unitario;

                return {
                    ...producto,
                    cantidad_devuelta: nuevaCantidad,
                    monto_devolucion: montoDevolucion,
                    seleccionado: nuevaCantidad > 0
                };
            }
            return producto;
        }));
    };

    // Actualizar motivo de devolución
    const actualizarMotivo = (idProducto, motivo) => {
        setProductosDevolucion(prev => prev.map(producto =>
            producto.id_producto === idProducto
                ? { ...producto, motivo }
                : producto
        ));
    };

    // Alternar selección de producto
    const toggleSeleccionProducto = (idProducto, seleccionado) => {
        setProductosDevolucion(prev => prev.map(producto => {
            if (producto.id_producto === idProducto) {
                return {
                    ...producto,
                    seleccionado,
                    cantidad_devuelta: seleccionado ? producto.cantidad_devuelta : 0,
                    monto_devolucion: seleccionado ? producto.monto_devolucion : 0
                };
            }
            return producto;
        }));
    };

    // Calcular monto total de devolución
    const calcularMontoTotal = () => {
        return productosDevolucion
            .filter(p => p.seleccionado && p.cantidad_devuelta > 0)
            .reduce((total, p) => total + p.monto_devolucion, 0);
    };

    // Limpiar formulario y resetear estados
    const limpiarFormulario = () => {
        setIdVentaBuscar('');
        setVentaBuscada(null);
        setProductosDevolucion([]);
        setFormaDevolucion('efectivo');
        setError('');
        setMostrarConfirmacion(false);
        setHistorialDevoluciones([]);
        setDevolucionesPrevias({});
        setEstadisticasVenta({
            total_productos_originales: 0,
            total_monto_original: 0,
            total_devuelto_historico: 0,
            porcentaje_devuelto: 0
        });
    };

    // Validar formulario con validaciones mejoradas
    const validarFormulario = () => {
        const productosSeleccionados = productosDevolucion.filter(p => p.seleccionado && p.cantidad_devuelta > 0);

        if (productosSeleccionados.length === 0) {
            setError('Debes seleccionar al menos un producto para devolver');
            return false;
        }

        for (const producto of productosSeleccionados) {
            // Validar que el producto no esté completamente devuelto
            if (producto.estado_devolucion === 'completamente_devuelto') {
                setError(`${producto.nombre_producto} ya está completamente devuelto`);
                return false;
            }

            if (producto.cantidad_devuelta <= 0) {
                setError(`La cantidad a devolver de ${producto.nombre_producto} debe ser mayor a 0`);
                return false;
            }

            // Validar contra cantidad disponible (no cantidad original)
            if (producto.cantidad_devuelta > producto.cantidad_disponible) {
                setError(`La cantidad a devolver de ${producto.nombre_producto} no puede ser mayor a la cantidad disponible para devolución (${producto.cantidad_disponible})`);
                return false;
            }

            if (!producto.motivo.trim()) {
                setError(`El motivo es obligatorio para ${producto.nombre_producto}`);
                return false;
            }
        }

        if (!formaDevolucion) {
            setError('Debes seleccionar una forma de devolución');
            return false;
        }

        // Validar cuenta corriente solo si tiene cliente
        if (formaDevolucion === 'cuenta_corriente' && !ventaBuscada.cliente) {
            setError('Esta venta no tiene cliente asignado. No se puede acreditar a cuenta corriente.');
            return false;
        }

        setError('');
        return true;
    };

    // Procesar devolución
    const procesarDevolucion = async () => {
        if (!validarFormulario()) return;

        setProcesando(true);
        setError('');

        try {
            const productosParaEnviar = productosDevolucion
                .filter(p => p.seleccionado && p.cantidad_devuelta > 0)
                .map(p => ({
                    id_producto: p.id_producto,
                    cantidad_devuelta: p.cantidad_devuelta,
                    motivo: p.motivo
                }));

            const requestBody = {
                id_venta: ventaBuscada.id,
                productos_devueltos: productosParaEnviar,
                forma_devolucion: formaDevolucion,
                usuario_id: user?.id_user || 1 // Usar el ID del usuario del contexto o 1 como fallback
            };

            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/devoluciones`,
                requestBody,
                {
                    headers: {
                        'x-auth-token': token,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // Éxito
            alert(`Devolución procesada correctamente. Monto total devuelto: $${response.data.total_devuelto}`);

            // Recargar la información de la venta para mostrar los cambios
            await buscarVenta(ventaBuscada.id);

            // Limpiar selecciones del formulario pero mantener la venta cargada
            setMostrarConfirmacion(false);

            // Opcional: redirigir al historial
            // navigate('/historial-ventas');

        } catch (err) {
            console.error('Error al procesar devolución:', err);

            if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError('Error al procesar la devolución. Intenta nuevamente.');
            }
        } finally {
            setProcesando(false);
        }
    };

    // Buscar automáticamente si viene con ID desde ruta
    useEffect(() => {
        if (id_venta) {
            buscarVenta(id_venta);
        }
    }, [id_venta]);

    const ResumenConfirmacion = () => {
        const productosSeleccionados = productosDevolucion.filter(p => p.seleccionado && p.cantidad_devuelta > 0);
        const montoTotal = calcularMontoTotal();

        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            >
                <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="bg-white rounded-lg p-6 max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto"
                >
                    <h3 className="text-xl font-bold mb-4 text-red-600">⚠️ Confirmar Devolución</h3>

                    {/* Información de la venta */}
                    <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded">
                        <div>
                            <p><strong>Venta ID:</strong> {ventaBuscada.id}</p>
                            <p><strong>Cliente:</strong> {ventaBuscada.cliente?.nombre || 'Consumidor final'}</p>
                            <p><strong>Fecha Venta:</strong> {new Date(ventaBuscada.createdAt).toLocaleDateString()}</p>
                        </div>
                        <div>
                            <p><strong>Forma de devolución:</strong> 
                                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                                    {formaDevolucion.replace('_', ' ').toUpperCase()}
                                </span>
                            </p>
                            <p><strong>Total Venta:</strong> ${ (estadisticasVenta.total_final ?? 0).toFixed(2) }</p>
                            {estadisticasVenta.total_antes && (
                                <p className="text-sm text-gray-500">Antes: ${(estadisticasVenta.total_antes).toFixed(2)}</p>
                            )}
                            <p><strong>Ya Devuelto Antes:</strong> <span className="text-red-600">${estadisticasVenta.total_ya_devuelto.toFixed(2)}</span></p>
                        </div>
                    </div>

                    <h4 className="font-semibold mb-3">Productos a devolver ({productosSeleccionados.length}):</h4>
                    <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                        {productosSeleccionados.map(producto => (
                            <div key={producto.id_producto} className="border border-red-200 p-3 rounded bg-red-50">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <p className="font-semibold">{producto.nombre_producto}</p>
                                        <div className="text-sm text-gray-600 mt-1">
                                            <p><strong>Cantidad a devolver:</strong> {producto.cantidad_devuelta.toFixed(3)} 
                                                <span className="text-gray-500"> (de {producto.cantidad_original.toFixed(3)} original)</span>
                                            </p>
                                            {producto.cantidad_ya_devuelta > 0 && (
                                                <p className="text-orange-600">
                                                    <strong>Ya devuelto antes:</strong> {producto.cantidad_ya_devuelta.toFixed(3)}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-sm mt-2">
                                            <p><strong>Precio unitario:</strong> ${producto.precio_unitario.toFixed(2)}</p>
                                            <p><strong>Motivo:</strong> <em>"{producto.motivo}"</em></p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-lg font-bold text-red-600">
                                            ${producto.monto_devolucion.toFixed(2)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Resumen financiero */}
                    <div className="border-t pt-4 mb-6">
                        <div className="bg-red-100 p-4 rounded">
                            <div className="flex justify-between items-center text-lg">
                                <span className="font-semibold">TOTAL A DEVOLVER:</span>
                                <span className="text-2xl font-bold text-red-600">${montoTotal.toFixed(2)}</span>
                            </div>
                            <div className="text-sm text-gray-600 mt-2">
                                <p>Nuevo total disponible para devolución: ${(estadisticasVenta.total_disponible_devolucion - montoTotal).toFixed(2)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Advertencias */}
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-sm text-yellow-800">
                            <strong>⚠️ Advertencia:</strong> Esta acción no se puede deshacer. 
                            El stock será restaurado automáticamente y se registrará el movimiento financiero.
                        </p>
                        {formaDevolucion === 'efectivo' && (
                            <p className="text-sm text-yellow-800 mt-1">
                                💰 Se realizará un egreso de caja por ${montoTotal.toFixed(2)}
                            </p>
                        )}
                        {formaDevolucion === 'cuenta_corriente' && (
                            <p className="text-sm text-yellow-800 mt-1">
                                📋 Se acreditará ${montoTotal.toFixed(2)} a la cuenta corriente del cliente
                            </p>
                        )}
                    </div>

                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={() => setMostrarConfirmacion(false)}
                            className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50"
                            disabled={procesando}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={procesarDevolucion}
                            className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
                            disabled={procesando}
                        >
                            {procesando ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Procesando...
                                </>
                            ) : (
                                <>✓ Confirmar Devolución</>
                            )}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto p-6">
                <Header onOpenHistory={() => navigate('/historial-devoluciones-avanzado')} />
                <SearchForm idVentaBuscar={idVentaBuscar} setIdVentaBuscar={setIdVentaBuscar} onBuscar={buscarVenta} loading={loading} />

                {/* Mostrar error mejorado */}
                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -50, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -50, scale: 0.95 }}
                            className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-xl shadow-lg mb-6 border-l-4 border-red-700"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                    <div className="bg-red-700 p-2 rounded-lg">
                                        <span className="text-xl">⚠️</span>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg">Error en la operación</h3>
                                        <p className="mt-1 opacity-90">{error}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setError('')} 
                                    className="text-white hover:text-red-200 text-2xl font-bold transition-colors duration-200 ml-4"
                                >
                                    ×
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Validación de caja abierta */}
                {verificandoCaja ? (
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
                        <div className="flex items-center justify-center gap-3">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <p className="text-gray-600">Verificando estado de caja...</p>
                        </div>
                    </div>
                ) : !cajaAbierta ? (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-xl shadow-lg mb-6 border-l-4 border-red-700"
                    >
                        <div className="flex items-start gap-4">
                            <div className="bg-red-700 p-3 rounded-lg">
                                <span className="text-3xl">🚫</span>
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold mb-2">No hay caja abierta</h3>
                                <p className="text-lg opacity-90 mb-4">
                                    Para procesar devoluciones necesitas tener una caja abierta. Las devoluciones en efectivo requieren registro en caja para el egreso correspondiente.
                                </p>
                                <div className="bg-red-600 bg-opacity-50 p-4 rounded-lg mb-4">
                                    <h4 className="font-semibold mb-2">¿Qué puedes hacer?</h4>
                                    <ul className="space-y-1 text-sm">
                                        <li>• Abre una caja desde el menú de gestión de caja</li>
                                        <li>• Contacta a un supervisor para abrir caja</li>
                                        <li>• Verifica que no tengas otra sesión con caja abierta</li>
                                    </ul>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={() => navigate('/caja')}
                                        className="px-6 py-3 bg-white text-red-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                                    >
                                        <span className="text-lg">🏦</span>
                                        Gestión de Caja
                                    </button>
                                    <button
                                        onClick={() => window.location.reload()}
                                        className="px-6 py-3 bg-red-700 text-white font-semibold rounded-lg hover:bg-red-800 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                                    >
                                        <span className="text-lg">🔄</span>
                                        Verificar Nuevamente
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : null}

                {/* Solo mostrar el resto del contenido si hay caja abierta */}
                {cajaAbierta && (
                    <>
                        {/* Indicador de caja abierta */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 mb-6">
                            <div className="flex items-center gap-3">
                                <div className="bg-green-500 p-2 rounded-lg">
                                    <span className="text-white text-lg">💰</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-green-800">Caja Abierta - {cajaAbierta.nombre}</h3>
                                    <p className="text-sm text-green-600">
                                        Abierta el {new Date(cajaAbierta.fecha_apertura).toLocaleString('es-AR')} 
                                        {cajaAbierta.usuario_apertura && ` por ${cajaAbierta.usuario_apertura.nombre_user}`}
                                    </p>
                                </div>
                                <div className="bg-green-100 px-3 py-1 rounded-full">
                                    <span className="text-green-700 font-medium text-sm">✅ Activa</span>
                                </div>
                            </div>
                        </div>

                {/* Información de la venta mejorada con estadísticas visuales */}
                {ventaBuscada && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100"
                    >
                        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
                            <div className="flex items-center gap-3">
                                <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-3 rounded-lg">
                                    <span className="text-white text-2xl">📋</span>
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-gray-800">Información de la Venta</h2>
                                    <p className="text-gray-600">Detalles y estado de devoluciones</p>
                                </div>
                            </div>
                            
                            {historialDevoluciones.length > 0 && (
                                <button
                                    onClick={() => setMostrarHistorial(!mostrarHistorial)}
                                    className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                                >
                                    <span className="flex items-center gap-2">
                                        {mostrarHistorial ? '🙈 Ocultar' : '👁️ Ver'} Historial ({historialDevoluciones.length})
                                    </span>
                                </button>
                            )}
                        </div>
                        
                        {/* Información básica en cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-blue-500 p-2 rounded-lg">
                                        <span className="text-white text-lg">🏷️</span>
                                    </div>
                                    <div>
                                        <p className="text-sm text-blue-600 font-medium">ID Venta</p>
                                        <p className="text-xl font-bold text-blue-800">#{ventaBuscada.id}</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-purple-500 p-2 rounded-lg">
                                        <span className="text-white text-lg">📅</span>
                                    </div>
                                    <div>
                                        <p className="text-sm text-purple-600 font-medium">Fecha</p>
                                        <p className="text-lg font-bold text-purple-800">
                                            {new Date(ventaBuscada.createdAt).toLocaleDateString('es-AR')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-lg border border-amber-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-amber-500 p-2 rounded-lg">
                                        <span className="text-white text-lg">👤</span>
                                    </div>
                                    <div>
                                        <p className="text-sm text-amber-600 font-medium">Cliente</p>
                                        <p className="text-lg font-bold text-amber-800 truncate">
                                            {ventaBuscada.cliente?.nombre || 'Consumidor final'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-lg border border-teal-200">
                                <div className="flex items-center gap-3">
                                    <div className="bg-teal-500 p-2 rounded-lg">
                                        <span className="text-white text-lg">👨‍💼</span>
                                    </div>
                                    <div>
                                        <p className="text-sm text-teal-600 font-medium">Vendedor</p>
                                        <p className="text-lg font-bold text-teal-800 truncate">
                                            {ventaBuscada.usuario?.nombre_user || 'Sin vendedor'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Estadísticas financieras mejoradas */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="p-4 rounded border border-gray-100 bg-white">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Total (final)</p>
                                    <p className="text-xl font-bold text-gray-900">${(estadisticasVenta.total_final ?? 0).toFixed(2)}</p>
                                    {estadisticasVenta.total_antes && (
                                      <p className="text-sm text-gray-500 line-through">Antes: ${(estadisticasVenta.total_antes).toFixed(2)}</p>
                                    )}
                                </div>
                            </div>

                            <div className="p-4 rounded border border-gray-100 bg-white">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Ya Devuelto</p>
                                    <p className="text-xl font-bold text-gray-900">${(estadisticasVenta.total_ya_devuelto ?? 0).toFixed(2)}</p>
                                    <p className="text-xs text-gray-500 mt-1">{historialDevoluciones.length} devolución(es)</p>
                                </div>
                            </div>

                            <div className="p-4 rounded border border-gray-100 bg-white">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Disponible</p>
                                    <p className="text-xl font-bold text-gray-900">${(estadisticasVenta.total_disponible_devolucion ?? 0).toFixed(2)}</p>
                                    <p className="text-xs text-gray-500 mt-1">Para nuevas devoluciones</p>
                                </div>
                            </div>
                        </div>

                        {/* Historial de devoluciones colapsable mejorado */}
                        <AnimatePresence>
                            {mostrarHistorial && historialDevoluciones.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="border-t-2 border-gray-200 pt-6"
                                >
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="bg-gradient-to-r from-orange-500 to-red-500 p-2 rounded-lg">
                                            <span className="text-white text-lg">📈</span>
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-800">Historial de Devoluciones</h3>
                                    </div>
                                    
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                                        {historialDevoluciones.map((devolucion, index) => (
                                            <motion.div 
                                                key={index}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200"
                                            >
                                                <div className="flex justify-between items-start mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-lg">🔄</span>
                                                        <span className="font-semibold text-gray-800">Devolución #{index + 1}</span>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-lg font-bold text-red-600">
                                                            ${parseFloat(devolucion.monto_devuelto).toFixed(2)}
                                                        </p>
                                                        <span className={`px-2 py-1 rounded text-xs ${
                                                            devolucion.forma_devolucion === 'efectivo' ? 'bg-green-100 text-green-700' :
                                                            devolucion.forma_devolucion === 'cuenta_corriente' ? 'bg-blue-100 text-blue-700' :
                                                            'bg-purple-100 text-purple-700'
                                                        }`}>
                                                            {devolucion.forma_devolucion?.replace('_', ' ').toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                
                                                <div className="space-y-2 text-sm">
                                                    <p><span className="font-medium text-gray-600">📅 Fecha:</span> {new Date(devolucion.createdAt).toLocaleString('es-AR')}</p>
                                                    <p><span className="font-medium text-gray-600">📦 Producto:</span> {devolucion.producto?.nombre_producto}</p>
                                                    <p><span className="font-medium text-gray-600">📊 Cantidad:</span> {devolucion.cantidad_devuelta}</p>
                                                    {devolucion.motivo && (
                                                        <p className="bg-yellow-50 p-2 rounded border border-yellow-200">
                                                            <span className="font-medium text-gray-600">💭 Motivo:</span> 
                                                            <em className="text-gray-700"> "{devolucion.motivo}"</em>
                                                        </p>
                                                    )}
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}

                {/* Lista de productos mejorada con diseño moderno */}
                {productosDevolucion.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-3 rounded-lg">
                                <span className="text-white text-2xl">📦</span>
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">Productos de la Venta</h2>
                                <p className="text-gray-600">Selecciona los productos y cantidades a devolver</p>
                            </div>
                        </div>

                        {/* Vista responsive: Cards en móvil, tabla en desktop */}
                        <div className="hidden lg:block">
                            {/* Tabla para pantallas grandes */}
                            <div className="overflow-x-auto bg-gray-50 rounded-lg">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-gray-100 to-gray-200">
                                            <th className="text-left p-4 font-semibold text-gray-700">✅</th>
                                            <th className="text-left p-4 font-semibold text-gray-700">Producto</th>
                                            <th className="text-left p-4 font-semibold text-gray-700">Estado</th>
                                            <th className="text-left p-4 font-semibold text-gray-700">Cantidades</th>
                                            <th className="text-left p-4 font-semibold text-gray-700">Precio</th>
                                            <th className="text-left p-4 font-semibold text-gray-700">A Devolver</th>
                                            <th className="text-left p-4 font-semibold text-gray-700">Motivo</th>
                                            <th className="text-left p-4 font-semibold text-gray-700">Monto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productosDevolucion.map((producto, index) => {
                                            const estadoConfig = {
                                                'sin_devolver': { 
                                                    color: 'text-green-600', 
                                                    bg: 'bg-green-50 border-green-200', 
                                                    icon: '✅',
                                                    text: 'Disponible' 
                                                },
                                                'parcialmente_devuelto': { 
                                                    color: 'text-yellow-600', 
                                                    bg: 'bg-yellow-50 border-yellow-200', 
                                                    icon: '⚠️',
                                                    text: 'Parcial' 
                                                },
                                                'completamente_devuelto': { 
                                                    color: 'text-red-600', 
                                                    bg: 'bg-red-50 border-red-200', 
                                                    icon: '❌',
                                                    text: 'Completo' 
                                                }
                                            }[producto.estado_devolucion] || { 
                                                color: 'text-gray-600', 
                                                bg: 'bg-gray-50 border-gray-200', 
                                                icon: '❓',
                                                text: 'Desconocido' 
                                            };

                                            return (
                                                <motion.tr 
                                                    key={producto.id_producto}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: index * 0.05 }}
                                                    className={`border-b transition-all duration-200 hover:bg-blue-50 ${
                                                        producto.deshabilitado ? 'opacity-60' : ''
                                                    } ${producto.seleccionado ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}
                                                >
                                                    <td className="p-4">
                                                        <label className="flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={producto.seleccionado}
                                                                onChange={(e) => toggleSeleccionProducto(producto.id_producto, e.target.checked)}
                                                                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
                                                                disabled={producto.deshabilitado}
                                                            />
                                                            <span className="ml-2 text-sm text-gray-600">
                                                                {producto.seleccionado ? 'Seleccionado' : 'Seleccionar'}
                                                            </span>
                                                        </label>
                                                    </td>
                                                    
                                                    <td className="p-4">
                                                        <div>
                                                            <p className="font-semibold text-gray-800">{producto.nombre_producto}</p>
                                                            {producto.devoluciones_previas.length > 0 && (
                                                                <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                                                                    🔄 {producto.devoluciones_previas.length} devolución(es) previa(s)
                                                                </p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    
                                                    <td className="p-4">
                                                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${estadoConfig.bg} ${estadoConfig.color}`}>
                                                            <span>{estadoConfig.icon}</span>
                                                            {estadoConfig.text}
                                                        </div>
                                                    </td>
                                                    
                                                    <td className="p-4">
                                                        <div className="space-y-1 text-sm">
                                                            <p><span className="text-gray-600">Original:</span> <span className="font-mono font-semibold">{producto.cantidad_original.toFixed(3)}</span></p>
                                                            {producto.cantidad_ya_devuelta > 0 && (
                                                                <p><span className="text-red-600">Devuelto:</span> <span className="font-mono font-semibold text-red-600">{producto.cantidad_ya_devuelta.toFixed(3)}</span></p>
                                                            )}
                                                            <p><span className="text-blue-600">Disponible:</span> <span className="font-mono font-semibold text-blue-600">{producto.cantidad_disponible.toFixed(3)}</span></p>
                                                        </div>
                                                    </td>
                                                    
                                                    <td className="p-4">
                                                        <p className="font-mono font-semibold text-gray-800">${producto.precio_unitario.toFixed(2)}</p>
                                                    </td>
                                                    
                                                    <td className="p-4">
                                                        <div className="space-y-2">
                                                            <input
                                                                type="number"
                                                                value={producto.cantidad_devuelta}
                                                                onChange={(e) => actualizarCantidadDevolucion(producto.id_producto, e.target.value)}
                                                                max={producto.cantidad_disponible}
                                                                min="0"
                                                                step="0.001"
                                                                className={`w-28 px-3 py-2 border-2 rounded-lg font-mono text-sm transition-all duration-200 ${
                                                                    producto.seleccionado && !producto.deshabilitado 
                                                                        ? 'border-blue-300 focus:border-blue-500 focus:ring focus:ring-blue-200' 
                                                                        : 'border-gray-200 bg-gray-50'
                                                                }`}
                                                                disabled={!producto.seleccionado || producto.deshabilitado}
                                                                placeholder={producto.deshabilitado ? "N/A" : "0.000"}
                                                            />
                                                            {producto.cantidad_devuelta > producto.cantidad_disponible && (
                                                                <p className="text-xs text-red-500 flex items-center gap-1">
                                                                    ⚠️ Máximo: {producto.cantidad_disponible.toFixed(3)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </td>
                                                    
                                                    <td className="p-4">
                                                        <input
                                                            type="text"
                                                            value={producto.motivo}
                                                            onChange={(e) => actualizarMotivo(producto.id_producto, e.target.value)}
                                                            placeholder="Motivo de devolución"
                                                            className={`w-40 px-3 py-2 border-2 rounded-lg text-sm transition-all duration-200 ${
                                                                producto.seleccionado && !producto.deshabilitado 
                                                                    ? 'border-blue-300 focus:border-blue-500 focus:ring focus:ring-blue-200' 
                                                                    : 'border-gray-200 bg-gray-50'
                                                            }`}
                                                            disabled={!producto.seleccionado || producto.deshabilitado}
                                                        />
                                                    </td>
                                                    
                                                    <td className="p-4">
                                                        <p className={`font-mono font-bold text-lg ${
                                                            producto.monto_devolucion > 0 ? 'text-red-600' : 'text-gray-400'
                                                        }`}>
                                                            ${producto.monto_devolucion.toFixed(2)}
                                                        </p>
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Vista de cards para móviles */}
                        <div className="lg:hidden space-y-4">
                            {productosDevolucion.map((producto, index) => {
                                const estadoConfig = {
                                    'sin_devolver': { 
                                        color: 'text-green-600', 
                                        bg: 'bg-green-50 border-green-200', 
                                        icon: '✅',
                                        text: 'Disponible' 
                                    },
                                    'parcialmente_devuelto': { 
                                        color: 'text-yellow-600', 
                                        bg: 'bg-yellow-50 border-yellow-200', 
                                        icon: '⚠️',
                                        text: 'Parcial' 
                                    },
                                    'completamente_devuelto': { 
                                        color: 'text-red-600', 
                                        bg: 'bg-red-50 border-red-200', 
                                        icon: '❌',
                                        text: 'Completo' 
                                    }
                                }[producto.estado_devolucion] || { 
                                    color: 'text-gray-600', 
                                    bg: 'bg-gray-50 border-gray-200', 
                                    icon: '❓',
                                    text: 'Desconocido' 
                                };

                                return (
                                    <motion.div
                                        key={producto.id_producto}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                        className={`border-2 rounded-xl p-4 transition-all duration-200 ${
                                            producto.seleccionado ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
                                        } ${producto.deshabilitado ? 'opacity-60' : ''}`}
                                    >
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-800 text-lg">{producto.nombre_producto}</h3>
                                                {producto.devoluciones_previas.length > 0 && (
                                                    <p className="text-sm text-orange-600 mt-1 flex items-center gap-1">
                                                        🔄 {producto.devoluciones_previas.length} devolución(es) previa(s)
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className={`px-3 py-1 rounded-full text-sm font-medium border ${estadoConfig.bg} ${estadoConfig.color}`}>
                                                    <span className="flex items-center gap-1">
                                                        {estadoConfig.icon} {estadoConfig.text}
                                                    </span>
                                                </div>
                                                <label className="flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={producto.seleccionado}
                                                        onChange={(e) => toggleSeleccionProducto(producto.id_producto, e.target.checked)}
                                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                                        disabled={producto.deshabilitado}
                                                    />
                                                </label>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div className="space-y-2">
                                                <p className="text-sm text-gray-600">Cantidades:</p>
                                                <div className="space-y-1 text-sm">
                                                    <p><span className="text-gray-600">Original:</span> <span className="font-mono font-semibold">{producto.cantidad_original.toFixed(3)}</span></p>
                                                    {producto.cantidad_ya_devuelta > 0 && (
                                                        <p><span className="text-red-600">Devuelto:</span> <span className="font-mono font-semibold text-red-600">{producto.cantidad_ya_devuelta.toFixed(3)}</span></p>
                                                    )}
                                                    <p><span className="text-blue-600">Disponible:</span> <span className="font-mono font-semibold text-blue-600">{producto.cantidad_disponible.toFixed(3)}</span></p>
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <p className="text-sm text-gray-600">Precio y monto:</p>
                                                <p><span className="text-gray-600">Precio:</span> <span className="font-mono font-semibold">${producto.precio_unitario.toFixed(2)}</span></p>
                                                <p><span className="text-gray-600">Monto:</span> <span className={`font-mono font-bold ${producto.monto_devolucion > 0 ? 'text-red-600' : 'text-gray-400'}`}>${producto.monto_devolucion.toFixed(2)}</span></p>
                                            </div>
                                        </div>

                                        {producto.seleccionado && !producto.deshabilitado && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                className="space-y-3 pt-3 border-t border-gray-200"
                                            >
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Cantidad a devolver:</label>
                                                    <input
                                                        type="number"
                                                        value={producto.cantidad_devuelta}
                                                        onChange={(e) => actualizarCantidadDevolucion(producto.id_producto, e.target.value)}
                                                        max={producto.cantidad_disponible}
                                                        min="0"
                                                        step="0.001"
                                                        className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg font-mono focus:border-blue-500 focus:ring focus:ring-blue-200"
                                                        placeholder="0.000"
                                                    />
                                                    {producto.cantidad_devuelta > producto.cantidad_disponible && (
                                                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                                                            ⚠️ Máximo disponible: {producto.cantidad_disponible.toFixed(3)}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Motivo de devolución:</label>
                                                    <input
                                                        type="text"
                                                        value={producto.motivo}
                                                        onChange={(e) => actualizarMotivo(producto.id_producto, e.target.value)}
                                                        placeholder="Describe el motivo de la devolución"
                                                        className="w-full px-3 py-2 border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring focus:ring-blue-200"
                                                    />
                                                </div>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Formas de devolución mejoradas */}
                        <div className="mt-8 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-lg">
                                    <span className="text-white text-xl">💳</span>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">Forma de Devolución</h3>
                                    <p className="text-gray-600">Selecciona cómo deseas procesar la devolución</p>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <label className={`cursor-pointer transition-all duration-200 ${
                                    formaDevolucion === 'efectivo' ? 'transform scale-105' : 'hover:scale-102'
                                }`}>
                                    <div className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                                        formaDevolucion === 'efectivo' 
                                            ? 'border-green-400 bg-green-50 shadow-lg' 
                                            : 'border-gray-200 bg-white hover:border-green-300 hover:shadow-md'
                                    }`}>
                                        <div className="flex items-center gap-3 mb-2">
                                            <input
                                                type="radio"
                                                value="efectivo"
                                                checked={formaDevolucion === 'efectivo'}
                                                onChange={(e) => setFormaDevolucion(e.target.value)}
                                                className="w-5 h-5 text-green-600"
                                            />
                                            <div className="bg-green-500 p-2 rounded-lg">
                                                <span className="text-white text-lg">💵</span>
                                            </div>
                                            <span className="font-semibold text-gray-800">Efectivo</span>
                                        </div>
                                        <p className="text-sm text-gray-600 ml-8">
                                            Se realizará un egreso de caja por el monto devuelto
                                        </p>
                                    </div>
                                </label>

                                <label className="transition-all duration-200 opacity-50 cursor-not-allowed">
                                    <div className={`p-4 rounded-xl border-2 transition-all duration-200 border-gray-200 bg-gray-50`}>
                                        <div className="flex items-center gap-3 mb-2">
                                            <input
                                                type="radio"
                                                value="cuenta_corriente"
                                                checked={formaDevolucion === 'cuenta_corriente'}
                                                onChange={(e) => setFormaDevolucion(e.target.value)}
                                                className="w-5 h-5 text-blue-600"
                                                disabled={true}
                                            />
                                            <div className="bg-gray-400 p-2 rounded-lg">
                                                <span className="text-white text-lg">📋</span>
                                            </div>
                                            <span className="font-semibold text-gray-800">Cuenta Corriente</span>
                                        </div>
                                        <p className="text-sm text-gray-600 ml-8">
                                            Próximamente disponible - En desarrollo
                                        </p>
                                    </div>
                                </label>

                                <label className="transition-all duration-200 opacity-50 cursor-not-allowed">
                                    <div className={`p-4 rounded-xl border-2 transition-all duration-200 border-gray-200 bg-gray-50`}>
                                        <div className="flex items-center gap-3 mb-2">
                                            <input
                                                type="radio"
                                                value="nota_credito"
                                                checked={formaDevolucion === 'nota_credito'}
                                                onChange={(e) => setFormaDevolucion(e.target.value)}
                                                className="w-5 h-5 text-purple-600"
                                                disabled={true}
                                            />
                                            <div className="bg-gray-400 p-2 rounded-lg">
                                                <span className="text-white text-lg">🎫</span>
                                            </div>
                                            <span className="font-semibold text-gray-800">Nota de Crédito</span>
                                        </div>
                                        <p className="text-sm text-gray-600 ml-8">
                                            Próximamente disponible - En desarrollo
                                        </p>
                                    </div>
                                </label>
                            </div>
                        </div>

                    {/* Resumen y botones de acción mejorados */}
                    <div className="mt-8 border-t border-gray-200 pt-6">
                        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 mb-6">
                            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl">📊</span>
                                        <h3 className="text-lg font-bold text-gray-800">Resumen de Devolución</h3>
                                    </div>
                                    <p className="text-gray-600 flex items-center gap-2">
                                        <span className="font-semibold">Productos seleccionados:</span>
                                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-bold">
                                            {productosDevolucion.filter(p => p.seleccionado && p.cantidad_devuelta > 0).length}
                                        </span>
                                    </p>
                                    <p className="text-2xl font-bold text-red-600 flex items-center gap-2">
                                        <span className="text-gray-600 text-lg font-semibold">Total a devolver:</span>
                                        <span className="font-mono">${calcularMontoTotal().toFixed(2)}</span>
                                    </p>
                                </div>
                                
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <motion.button
                                        type="button"
                                        onClick={limpiarFormulario}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="px-6 py-3 bg-gradient-to-r from-gray-400 to-gray-500 text-white font-semibold rounded-xl hover:from-gray-500 hover:to-gray-600 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                                    >
                                        <span className="text-lg">🔄</span>
                                        Limpiar Formulario
                                    </motion.button>
                                    
                                    <motion.button
                                        onClick={() => setMostrarConfirmacion(true)}
                                        whileHover={calcularMontoTotal() > 0 && productosDevolucion.some(p => p.seleccionado && p.cantidad_devuelta > 0) ? { scale: 1.02 } : {}}
                                        whileTap={calcularMontoTotal() > 0 && productosDevolucion.some(p => p.seleccionado && p.cantidad_devuelta > 0) ? { scale: 0.98 } : {}}
                                        className={`px-6 py-3 font-semibold rounded-xl transition-all duration-200 shadow-lg flex items-center justify-center gap-2 ${
                                            calcularMontoTotal() === 0 || 
                                            !productosDevolucion.some(p => p.seleccionado && p.cantidad_devuelta > 0) ||
                                            productosDevolucion.filter(p => p.seleccionado && p.cantidad_devuelta > 0).some(p => !p.motivo.trim())
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 hover:shadow-xl'
                                        }`}
                                        disabled={
                                            calcularMontoTotal() === 0 || 
                                            !productosDevolucion.some(p => p.seleccionado && p.cantidad_devuelta > 0) ||
                                            productosDevolucion.filter(p => p.seleccionado && p.cantidad_devuelta > 0).some(p => !p.motivo.trim())
                                        }
                                    >
                                        <span className="text-lg">📤</span>
                                        Procesar Devolución
                                    </motion.button>
                                </div>
                            </div>
                            
                            {/* Indicadores de validación */}
                            {calcularMontoTotal() === 0 || !productosDevolucion.some(p => p.seleccionado && p.cantidad_devuelta > 0) ? (
                                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                                    <span className="text-yellow-600">⚠️</span>
                                    <p className="text-sm text-yellow-700">
                                        Selecciona al menos un producto con cantidad mayor a 0 para continuar
                                    </p>
                                </div>
                            ) : productosDevolucion.filter(p => p.seleccionado && p.cantidad_devuelta > 0).some(p => !p.motivo.trim()) ? (
                                <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
                                    <span className="text-orange-600">📝</span>
                                    <p className="text-sm text-orange-700">
                                        Completa el motivo de devolución para todos los productos seleccionados
                                    </p>
                                </div>
                            ) : (
                                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                                    <span className="text-green-600">✅</span>
                                    <p className="text-sm text-green-700">
                                        Todo listo para procesar la devolución
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Modal de confirmación */}
            <AnimatePresence>
                {mostrarConfirmacion && (
                    <ConfirmModal
                        ventaBuscada={ventaBuscada}
                        estadisticasVenta={estadisticasVenta}
                        productosSeleccionados={productosDevolucion.filter(p => p.seleccionado && p.cantidad_devuelta > 0)}
                        montoTotal={calcularMontoTotal()}
                        formaDevolucion={formaDevolucion}
                        procesando={procesando}
                        onCancel={() => setMostrarConfirmacion(false)}
                        onConfirm={procesarDevolucion}
                    />
                )}
            </AnimatePresence>
                    </>
                )}
            </div>
        </div>
    );
};

export default DevolucionVenta;
